import { Box, Typography, Button } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { IconLock, IconCreditCard, IconUsers, IconShieldCheck } from '@tabler/icons-react';
import GoogleIcon from './GoogleIcon';

// Hero left column: eyebrow, split-color H1, subcopy, three value props, and the
// primary white "Continue with Google" CTA. Theme-native; the white CTA fill is
// the canonical Google-button brand treatment (documented exception, paired with
// the 4-color G so it reads as Google-account auth).
const VALUE_PROPS = [
  {
    icon: IconCreditCard,
    color: (t) => t.palette.cat.checking,
    title: 'Budgets and ledgers in one place',
    desc: 'Sub-second balance lookups across years of transactions.',
  },
  {
    icon: IconUsers,
    color: (t) => t.palette.interactive,
    title: 'From solo to small business',
    desc: 'Individuals, full households with dependents, or a small business. Invite people and share only what you choose.',
  },
  {
    icon: IconShieldCheck,
    color: (t) => t.palette.success.main,
    title: 'Private by default',
    desc: 'Never shared, never sold, never disclosed absent a valid court order.',
  },
];

const HeroCopy = ({ onSignIn, isLoaded }) => {
  const theme = useTheme();

  return (
    <Box sx={{ maxWidth: 520 }}>
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
          mb: 3,
          px: 1.25,
          py: 0.5,
          borderRadius: 999,
          bgcolor: alpha(theme.palette.interactive, 0.12),
          border: `1px solid ${alpha(theme.palette.interactive, 0.25)}`,
        }}
      >
        <Box aria-hidden sx={{ display: 'inline-flex', color: theme.palette.interactive }}>
          <IconLock size={13} />
        </Box>
        <Box
          component="span"
          sx={{ fontWeight: 600, fontSize: 11, letterSpacing: '0.14em', color: theme.palette.interactive }}
        >
          PRIVATE BY DESIGN
        </Box>
      </Box>

      <Typography
        component="h1"
        sx={{
          fontWeight: 700,
          fontSize: { xs: '2.25rem', sm: '2.75rem', md: '3.25rem' },
          lineHeight: 1.04,
          letterSpacing: '-0.015em',
          mb: 2.5,
        }}
      >
        Pragmatic finance that{' '}
        <Box component="span" sx={{ color: theme.palette.interactive }}>
          respects your data.
        </Box>
      </Typography>

      <Typography
        sx={{ fontSize: { xs: '1.0625rem', sm: '1.125rem' }, lineHeight: 1.55, color: theme.palette.text.secondary, maxWidth: 440, mb: 4 }}
      >
        Budgets, ledgers, and reconciliation in one tool, kept private by design.
      </Typography>

      <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, mb: 4, display: 'flex', flexDirection: 'column', gap: 2.25 }}>
        {VALUE_PROPS.map(({ icon: Icon, color, title, desc }) => {
          const accent = color(theme);
          return (
            <Box component="li" key={title} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.75 }}>
              <Box
                aria-hidden
                sx={{
                  flexShrink: 0,
                  width: 40,
                  height: 40,
                  borderRadius: '10px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: alpha(accent, 0.13),
                  color: accent,
                }}
              >
                <Icon size={20} />
              </Box>
              <Box>
                <Box sx={{ fontWeight: 600, fontSize: 16, color: theme.palette.text.primary, mb: 0.25 }}>{title}</Box>
                <Box sx={{ fontSize: 14, lineHeight: 1.5, color: theme.palette.text.dim }}>{desc}</Box>
              </Box>
            </Box>
          );
        })}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, flexWrap: 'wrap' }}>
        <Button
          onClick={onSignIn}
          disabled={!isLoaded}
          disableElevation
          startIcon={<GoogleIcon size={20} />}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            fontSize: 16,
            // Canonical Google sign-in button: white fill + near-black ink (brand
            // exception to the no-pure-white rule, paired with the 4-color G).
            bgcolor: '#fff',
            color: '#1f1f1f',
            borderRadius: '11px',
            px: '22px',
            py: '14px',
            boxShadow: `0 6px 20px ${alpha(theme.palette.surface.page, 0.35)}`,
            '&:hover': { bgcolor: '#f2f2f2', boxShadow: `0 6px 20px ${alpha(theme.palette.surface.page, 0.45)}` },
          }}
        >
          Continue with Google
        </Button>
        <Box sx={{ fontSize: 13, lineHeight: 1.5, color: theme.palette.text.dim, maxWidth: 150 }}>
          Beta is invite-only. Sign in to check your access.
        </Box>
      </Box>
    </Box>
  );
};

export default HeroCopy;
